import React, { useCallback, useMemo } from 'react';
import { Box } from '@mui/system';
import TranslationHelper from '@/app/helpers/TranslationHelper';
import { Button } from '@mui/material';
import { UserReportsFormValues } from './types';
import { ReportDefinitionRow } from './ReportDefinitionRow';
import { FormikErrors } from 'formik';
import { useReportTypes } from './hooks/useReportTypes';

type Props = {
    values: UserReportsFormValues;
    onChange: (values: UserReportsFormValues) => void;
    errors?: FormikErrors<UserReportsFormValues>;
    onReportTypeChange: (assignmentId: number) => void;
};

export const ReportsTab = ({
    values,
    onChange,
    errors,
    onReportTypeChange,
}: Props) => {
    const reportTypes = useReportTypes();
    const hasAccessToAnyReportType = reportTypes
        ? reportTypes.length > 0
        : undefined;

    const handleAdd = () => {
        const temporaryAssignmentId =
            -Math.floor(Math.random() * 100000000) - 1;
        const newValues = {
            ...values,
            [temporaryAssignmentId]: {
                name: undefined,
                typeId: undefined,
                templateId: undefined,
            },
        };
        onChange(newValues);
    };

    const handleDelete = useCallback(
        (assignmentId: number) => () => {
            const newValues = { ...values };
            delete newValues[assignmentId];
            onChange(newValues);
        },
        [onChange, values]
    );

    const rows = useMemo(() => {
        return Object.keys(values).map((id) => {
            const assignmentId = Number(id);
            const reportTypeId = values[assignmentId].typeId;
            return (
                <ReportDefinitionRow
                    reportTypes={reportTypes}
                    key={assignmentId}
                    assignmentId={assignmentId}
                    reportTypeId={reportTypeId}
                    onDelete={handleDelete(assignmentId)}
                    errors={errors}
                    onReportTypeChange={onReportTypeChange}
                />
            );
        });
    }, [errors, handleDelete, values, reportTypes]);

    return (
        <>
            {rows}
            <Box mt={2}>
                {hasAccessToAnyReportType !== undefined &&
                    (hasAccessToAnyReportType ? (
                        <Button variant="outlined" onClick={handleAdd}>
                            {TranslationHelper.translate('Add')}
                        </Button>
                    ) : (
                        <Box
                            pt={16}
                            textAlign="center"
                            color="text.secondary"
                            width={500}
                            margin={'auto'}
                        >
                            {TranslationHelper.translate(
                                'You cannot assign any report definition to a user because you do not have access to any report definition.'
                            )}
                        </Box>
                    ))}
            </Box>
        </>
    );
};
